import { appAlert, appConfirm, mountAppDialog } from '../utils/appDialog';
import {
  applyAppLocalStorageBackup,
  summarizeBackupKeys,
  type AppLocalStorageBackupKey,
} from '../utils/appLocalStorageBackup';
import { tryParseDeviceSettingsBackupBody } from '../utils/appLocalStorageBackupCrypto';
import { copyTextToClipboard } from '../utils/formatListClipboard';
import { MAX_SHARE_PARAM_CHARS } from '../utils/shareLinkCrypto';
import { decryptShareLinkPayload } from '../utils/shareLinkFlow';
import { ShareContentBlockedError, applyShareDecryptContentSecurityPolicy } from '../utils/shareContentSafety';
import type { ShareKind, SharePlainPayload } from '../utils/sharePayload';

function kindLabel(kind: ShareKind): string {
  if (kind === 'page') return 'Page';
  if (kind === 'section') return 'Section';
  return 'Item';
}

function renderDecrypted(result: HTMLElement, payload: SharePlainPayload): void {
  result.replaceChildren();
  result.classList.remove('hidden');

  const head = document.createElement('div');
  head.className = 'share-decrypt-result-head';

  const meta = document.createElement('p');
  meta.className = 'share-decrypt-meta';
  const metaParts = [kindLabel(payload.kind)];
  if (payload.title.trim()) metaParts.push(payload.title.trim());
  meta.textContent = metaParts.join(' · ');

  const copyBtn = document.createElement('button');
  copyBtn.type = 'button';
  copyBtn.className = 'btn-share-copy';
  copyBtn.title = 'Copy decrypted text to clipboard';
  copyBtn.textContent = '📋 Copy';

  head.append(meta, copyBtn);

  const deviceBackup = tryParseDeviceSettingsBackupBody(payload.body);
  if (deviceBackup) {
    const importBtn = document.createElement('button');
    importBtn.type = 'button';
    importBtn.className = 'btn-primary btn-sm';
    importBtn.textContent = 'Import device settings';
    head.append(importBtn);
    importBtn.addEventListener('click', () => {
      void (async () => {
        const keys = Object.keys(deviceBackup.entries) as AppLocalStorageBackupKey[];
        const ok = await appConfirm(
          `Import ${keys.length} setting(s) into this browser?\n\n${summarizeBackupKeys(keys)}\n\nReload the app after importing.`,
          { title: 'Import device settings?', confirmLabel: 'Import' }
        );
        if (!ok) return;
        try {
          applyAppLocalStorageBackup(deviceBackup, 'merge');
          await appAlert('Settings imported. Reload the page to apply everywhere.', 'Imported');
          window.location.assign('/');
        } catch (err) {
          await appAlert(
            err instanceof Error ? err.message : 'Import failed.',
            'Import failed'
          );
        }
      })();
    });
  }

  const body = document.createElement('pre');
  body.className = 'share-decrypt-body';
  body.textContent = payload.body;

  result.append(head, body);

  copyBtn.addEventListener('click', () => {
    void (async () => {
      try {
        await copyTextToClipboard(payload.body);
        const prev = copyBtn.textContent;
        copyBtn.textContent = 'Copied';
        copyBtn.disabled = true;
        window.setTimeout(() => {
          copyBtn.textContent = prev;
          copyBtn.disabled = false;
        }, 2000);
      } catch (err) {
        await appAlert(
          err instanceof Error ? err.message : 'Could not copy to clipboard.',
          'Copy failed'
        );
      }
    })();
  });
  result.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

function renderBlocked(result: HTMLElement, message: string): void {
  result.replaceChildren();
  result.classList.remove('hidden');
  const p = document.createElement('p');
  p.className = 'share-decrypt-error';
  p.textContent = message;
  result.append(p);
}

export class ShareDecryptView {
  static render(root: HTMLElement, encodedPayload: string, keyFromUrl: string | null): void {
    applyShareDecryptContentSecurityPolicy();
    mountAppDialog();
    const trimmed = encodedPayload.trim();
    if (!trimmed) {
      root.textContent = '';
      const wrap = document.createElement('div');
      wrap.className = 'share-decrypt';
      const err = document.createElement('p');
      err.className = 'share-decrypt-error';
      err.textContent = 'Missing share data in the link.';
      wrap.append(err);
      root.append(wrap);
      return;
    }
    if (trimmed.length > MAX_SHARE_PARAM_CHARS) {
      root.textContent = '';
      const wrap = document.createElement('div');
      wrap.className = 'share-decrypt';
      const err = document.createElement('p');
      err.className = 'share-decrypt-error';
      err.textContent = 'Share data is too long.';
      root.append(wrap);
      return;
    }

    const key = keyFromUrl?.trim() ?? '';

    root.replaceChildren();
    const shell = document.createElement('div');
    shell.className = 'share-decrypt';

    const header = document.createElement('header');
    header.className = 'share-decrypt-header';
    const title = document.createElement('h1');
    title.className = 'share-decrypt-title';
    title.textContent = 'View shared content';
    const lead = document.createElement('p');
    lead.className = 'share-decrypt-lead';
    lead.id = 'share-decrypt-lead';
    lead.textContent = 'Decrypting in your browser…';
    header.append(title, lead);

    const status = document.createElement('div');
    status.id = 'share-decrypt-status';
    status.className = 'share-decrypt-status';
    status.setAttribute('aria-live', 'polite');
    const spinner = document.createElement('span');
    spinner.className = 'app-spinner app-spinner--sm';
    spinner.setAttribute('aria-hidden', 'true');
    status.append(spinner);

    const form = document.createElement('form');
    form.className = 'share-decrypt-form hidden';
    form.id = 'share-decrypt-form';
    const label = document.createElement('label');
    label.className = 'share-decrypt-label';
    const input = document.createElement('input');
    input.type = 'password';
    input.id = 'share-passphrase';
    input.className = 'share-decrypt-input';
    input.autocomplete = 'off';
    label.append('Passphrase', document.createTextNode('\n'), input);
    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.className = 'btn-primary share-decrypt-submit';
    submit.textContent = 'Decrypt';
    form.append(label, submit);

    const result = document.createElement('div');
    result.id = 'share-decrypt-result';
    result.className = 'share-decrypt-result hidden';
    result.setAttribute('aria-live', 'polite');

    shell.append(header, status, form, result);
    root.append(shell);

    const showManualForm = () => {
      status.classList.add('hidden');
      form.classList.remove('hidden');
      lead.textContent =
        'Enter the passphrase from the person who sent you this link.';
      input.focus();
    };

    const runDecrypt = async (pass: string) => {
      status.classList.remove('hidden');
      status.replaceChildren();
      const busy = document.createElement('span');
      busy.className = 'app-spinner app-spinner--sm';
      busy.setAttribute('aria-hidden', 'true');
      status.append(busy);
      form.classList.add('hidden');
      result.classList.add('hidden');
      result.replaceChildren();
      lead.textContent = 'Decrypting in your browser…';
      try {
        const payload = await decryptShareLinkPayload(trimmed, pass);
        status.classList.add('hidden');
        lead.classList.add('hidden');
        renderDecrypted(result, payload);
      } catch (err) {
        status.classList.add('hidden');
        lead.classList.remove('hidden');
        if (err instanceof ShareContentBlockedError) {
          lead.textContent = 'This link contains blocked content.';
          renderBlocked(
            result,
            `${err.message} Shared text is shown as plain text only — scripts and HTML cannot run.`
          );
          return;
        }
        lead.textContent = 'Could not decrypt this link.';
        await appAlert(
          err instanceof Error ? err.message : 'Decryption failed.',
          'Could not decrypt'
        );
        if (!key) showManualForm();
      }
    };

    form.addEventListener('submit', e => {
      e.preventDefault();
      const pass = input.value.trim();
      if (!pass) return;
      void runDecrypt(pass);
    });

    if (key) {
      void runDecrypt(key);
    } else {
      showManualForm();
    }
  }
}
